import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

export const useGetSuburbQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_SUBURBS],
    queryFn: () => api.get(API_ROUTES.GET_SUBURBS),
  });
  return { data: data?.data?.data ?? [], isPending, isError, error };
};

// Full suburb document by id — GET /admin/suburb/:id. The list only carries
// suburb/postcode; the view/edit modals fetch the full doc on row click.
export const useGetSuburbByIdQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_SUBURBS, id],
    queryFn: () => api.get(`${API_ROUTES.GET_SUBURBS}/${id}`),
    enabled: !!id,
  });
  return { data: data?.data?.data ?? null, isPending, isError, error };
};

export const useCreateSuburbMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) => api.post(API_ROUTES.CREATE_SUBURB, data),
      onSuccess: () => {
        showToast("success", "Suburb created successfully");
      },
      onError: (error) => {
        const message =
          error?.response?.data?.message || "Failed to create suburb";
        showToast("error", message);
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_SUBURBS);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useUpdateSuburbMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.put(`${API_ROUTES.UPDATE_SUBURB}/${data?._id}`, { ...data }),
      onSuccess: () => {
        showToast("success", "Suburb updated successfully");
      },
      onError: (error) => {
        const message =
          error?.response?.data?.message || "Failed to update suburb";
        showToast("error", message);
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_SUBURBS);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useDeleteSuburbMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) => api.delete(`${API_ROUTES.DELETE_SUBURB}/${id}`),
      onSuccess: () => {
        showToast("success", "Suburb deleted successfully");
      },
      onError: (error) => {
        const message =
          error?.response?.data?.message || "Failed to delete suburb";
        showToast("error", message);
      },
      onSettled: () => {
        queryClient.invalidateQueries(API_ROUTES.GET_SUBURBS);
      },
    });
  return { mutate, isError, error, isLoading, isSuccess, isPending };
};